import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./ui/button";
import { ArrowRight, Play } from "lucide-react";

const Hero = () => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    setHasLoaded(true);
  }, []);

  const headline = "Get Paid Faster. Deny Less. Focus on Patients.";
  const words = headline.split(" ");

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20">
      {/* Animated background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f020_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f020_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />

      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 left-10 w-72 h-72 bg-[#14b8a6]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-96 h-96 bg-[#1e3a8a]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -40, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        style={{ y, opacity }}
        className="container mx-auto px-6 py-32 relative z-10"
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* Kinetic headline with line-by-line reveal */}
            <div className="mb-6 overflow-hidden">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-[#14b8a6] font-semibold text-sm tracking-wider uppercase mb-4"
              >
                Revenue Cycle Management
              </motion.div>
              <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 leading-tight">
                {words.map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, y: 50, rotateX: -90 }}
                    animate={hasLoaded ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                    transition={{
                      delay: index * 0.08,
                      duration: 0.8,
                      ease: [0.215, 0.61, 0.355, 1],
                    }}
                    className="inline-block mr-3 lg:mr-5"
                    style={{
                      transformOrigin: "center bottom",
                      display: "inline-block",
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
              </h1>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="text-xl text-slate-600 mb-10 leading-relaxed"
            >
              End-to-end medical billing, coding, and revenue cycle management
              that maximizes your practice revenue while you focus on patient
              care.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white px-8 py-6 text-lg rounded-full group transition-all duration-300 hover:shadow-2xl hover:shadow-[#1e3a8a]/30 hover:scale-105"
              >
                Get a Free Billing Analysis
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={scrollToContact}
                variant="outline"
                size="lg"
                className="border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white px-8 py-6 text-lg rounded-full group transition-all duration-300"
              >
                <Play className="mr-2 w-5 h-5" />
                Start Now
              </Button>
            </motion.div>
          </div>

          {/* Right Content - Dashboard Mockup with 3D effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
            className="relative"
          >
            <motion.div
              whileHover={{ scale: 1.02, rotateY: 5 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white"
              style={{
                transformStyle: "preserve-3d",
                perspective: "1000px",
              }}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1e3a8a]/20 via-transparent to-[#14b8a6]/20 opacity-50" />

              <img
                src="https://plus.unsplash.com/premium_photo-1661375173523-e53501f99674?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8bWVkaWNhbCUyMGJpbGxpbmd8ZW58MHx8MHx8fDA%3D"
                alt="Medical Billing Dashboard"
                className="w-full h-auto relative z-10"
              />

              {/* Floating data cards */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute top-8 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
              >
                <div className="text-sm text-slate-500">Clean Claims</div>
                <div className="text-2xl font-bold text-[#14b8a6]">98%</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="absolute bottom-8 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
              >
                <div className="text-sm text-slate-500">Revenue Increase</div>
                <div className="text-2xl font-bold text-[#1e3a8a]">+10%</div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
